import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { PageTitle, usePageData } from '../../../_metronic/layout/core'
import { getChartData, getStatistics, syncSessionStatistics, syncStatistics } from './api'
import type { DashboardChartViewMode } from './api'
import type { DashboardChartDataPoint } from './api'
import type { StatisticsModel } from './StatisticsModel'
import { StackedBarChart } from '../charts/components/chart/StackedBarChart'
import type { StackedBarChartPoint, StackedBarChartSeries } from '../charts/components/chart/StackedBarChart'
import { DatePicker } from '@progress/kendo-react-dateinputs'

type TimePeriod = 'today' | 'last7days' | 'lastmonth' | 'lastyear' | 'custom'
type TileMetricKey = 'activeKwh' | 'completedSessions' | 'revenueShare' | 'totalEnergy' | 'utilisationRate' | 'co2Emissions'
type MetricValueType = 'count' | 'currency' | 'kwh' | 'percent' | 'kg'
type ChartAxisMode = 'time' | 'day' | 'week' | 'month' | 'quarter' | 'year'
type DashboardStatisticsModel = StatisticsModel & {
  previousPeriodLabel?: string
  previousCompletedSessions?: string
  previousRevenueShare?: string
}

const cardStyle: React.CSSProperties = {
  background: '#f5f8fa',
  borderRadius: 12,
  padding: 18,
  boxShadow: '0 1px 4px #0001',
  border: '1px solid #ccc',
}

const filterControlStyle: React.CSSProperties = {
  padding: 8,
  borderRadius: 6,
  border: '1px solid #bbb'
}

const tileTitleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontWeight: 600,
  fontSize: 17,
}

const tileSubtitleStyle: React.CSSProperties = {
  color: '#444',
  fontSize: 13,
  lineHeight: 1.35,
}

const tileIconStyle = (color: string): React.CSSProperties => ({
  color,
  fontSize: 18,
})

const tileValueStyle = (color: string): React.CSSProperties => ({
  color,
  fontSize: '2.2em',
  margin: '4px 0',
  lineHeight: 1.1,
})

const parseNumber = (value?: string): number => {
  if (!value) return 0
  const parsed = Number(value.replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

const parseMetricValue = (rawValue: string | undefined, valueType: MetricValueType): number => {
  const value = parseNumber(rawValue)
  if (valueType === 'kg') {
    const normalized = (rawValue || '').trim().toLowerCase()
    if (normalized.endsWith('kg')) return value
    if (normalized.endsWith('g')) return value / 1000
    return value
  }
  if (valueType === 'count') return Math.round(value)
  return value
}

const toCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)

const toCount = (value: number, precision = 0) => Math.round(value).toLocaleString(undefined, { minimumFractionDigits: precision, maximumFractionDigits: precision });
const toDecimal = (value: number, precision = 0) => value.toLocaleString(undefined, { minimumFractionDigits: precision, maximumFractionDigits: precision });
const toPercent = (value: number) => `${value.toFixed()}%`
const toKg = (value: number, precision = 0) => `${value.toLocaleString(undefined, { minimumFractionDigits: precision, maximumFractionDigits: precision })} kg`

const formatOffsetTimestamp = (date: Date): string => {
  const pad = (value: number, length = 2) => `${Math.abs(value)}`.padStart(length, '0')
  const offsetMinutes = -date.getTimezoneOffset()
  const offsetSign = offsetMinutes >= 0 ? '+' : '-'
  const absoluteOffset = Math.abs(offsetMinutes)
  const offsetHours = Math.floor(absoluteOffset / 60)
  const offsetRemainingMinutes = absoluteOffset % 60

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}` +
    `${offsetSign}${pad(offsetHours)}:${pad(offsetRemainingMinutes)}`
}

const hexToHsl = (hex: string) => {
  const normalized = hex.replace('#', '')
  const safeHex = normalized.length === 3
    ? normalized.split('').map((character) => `${character}${character}`).join('')
    : normalized
  const red = parseInt(safeHex.slice(0, 2), 16) / 255
  const green = parseInt(safeHex.slice(2, 4), 16) / 255
  const blue = parseInt(safeHex.slice(4, 6), 16) / 255
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  let hue = 0
  let saturation = 0
  const lightness = (max + min) / 2

  if (max !== min) {
    const delta = max - min
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)
    switch (max) {
      case red:
        hue = (green - blue) / delta + (green < blue ? 6 : 0)
        break
      case green:
        hue = (blue - red) / delta + 2
        break
      default:
        hue = (red - green) / delta + 4
        break
    }
    hue /= 6
  }

  return { h: hue * 360, s: saturation * 100, l: lightness * 100 }
}

const buildRelatedColorPalette = (count: number, baseHex: string) => {
  const base = hexToHsl(baseHex)
  return Array.from({ length: count }, (_, index) => {
    const hueOffset = ((index % 6) - 2.5) * 8
    const ringOffset = Math.floor(index / 6)
    const hue = (base.h + hueOffset + 360) % 360
    const saturation = Math.max(52, Math.min(82, base.s + ((index % 2 === 0 ? 1 : -1) * 6)))
    const lightness = Math.max(34, Math.min(62, base.l + ((index % 3) - 1) * 7 + ringOffset * 3))
    return `hsl(${Math.round(hue)} ${Math.round(saturation)}% ${Math.round(lightness)}%)`
  })
}

const DashboardPage: React.FC = () => {
  const intl = useIntl()
  const { selectedSiteId, selectedSiteIds } = usePageData()
  const breadcrumbs = useMemo(() => [] as never[], [])
  const [statistics, setStatistics] = useState<DashboardStatisticsModel | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('last7days')
  const [chargerType, setChargerType] = useState<string>('All')
  const [chargerStatus, setChargerStatus] = useState<string>('All')
  const [customFromDate, setCustomFromDate] = useState<string>('')
  const [customToDate, setCustomToDate] = useState<string>('')
  const [customRangeError, setCustomRangeError] = useState<string>('')
  const [selectedMetric, setSelectedMetric] = useState<TileMetricKey>('completedSessions')
  const [chartSeries, setChartSeries] = useState<DashboardChartDataPoint[]>([])
  const [chartViewMode, setChartViewMode] = useState<DashboardChartViewMode>('consolidated')
  const [hoveredMetric, setHoveredMetric] = useState<TileMetricKey | null>(null)
  const [lastReloadAt, setLastReloadAt] = useState<Date | null>(null)
  const hasLoadedRef = useRef(false)
  const debounceTimerRef = useRef<number | null>(null)
  const suppressNextCustomChartFetchRef = useRef(false)
  const previousAutoFiltersRef = useRef({
    chargerType: 'All',
    chargerStatus: 'All',
    selectedSiteScopeKey: '',
    timePeriod: 'last7days' as TimePeriod,
  })
  const normalizedSelectedSiteIds = useMemo(() => {
    if (!Array.isArray(selectedSiteIds)) {
      return null
    }

    const normalized = Array.from(
      new Set(
        selectedSiteIds
          .map((siteId) => Number(siteId))
          .filter((siteId) => Number.isFinite(siteId))
      )
    ).sort((left, right) => left - right)

    return normalized.length > 0 ? normalized : null
  }, [selectedSiteIds])
  const selectedSiteScopeKey = useMemo(() => {
    if (selectedSiteId !== null) {
      return `single:${selectedSiteId}`
    }

    if (normalizedSelectedSiteIds !== null) {
      return `multi:${normalizedSelectedSiteIds.join(',')}`
    }

    return ''
  }, [normalizedSelectedSiteIds, selectedSiteId])
  const effectiveSingleSiteId = useMemo(() => {
    if (selectedSiteId !== null) {
      return selectedSiteId
    }

    if (normalizedSelectedSiteIds?.length === 1) {
      return normalizedSelectedSiteIds[0]
    }

    return null
  }, [normalizedSelectedSiteIds, selectedSiteId])
  const isDetailedViewAvailable = effectiveSingleSiteId !== null

  const formatDateInput = (date: Date) => {
    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const parseDateInput = (value: string): Date | null => {
    if (!value) return null
    const [yearText, monthText, dayText] = value.split('-')
    const year = Number(yearText)
    const month = Number(monthText)
    const day = Number(dayText)
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

    const parsed = new Date(year, month - 1, day)
    if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) return null
    return parsed
  }

  const getRangeForPeriod = useCallback((period: TimePeriod) => {
    const now = new Date()
    let from = new Date(now)
    switch (period) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'last7days':
        from = new Date(now)
        from.setDate(now.getDate() - 6)
        break
      case 'lastmonth':
        from = new Date(now)
        from.setMonth(now.getMonth() - 1)
        break
      case 'lastyear':
        from = new Date(now)
        from.setFullYear(now.getFullYear() - 1)
        break
      default:
        break
    }
    return { from, to: now }
  }, [])

  const computedRange = useMemo(() => {
    if (timePeriod === 'custom') return null
    return getRangeForPeriod(timePeriod)
  }, [getRangeForPeriod, timePeriod])

  const isCustomRange = timePeriod === 'custom'
  const fromPickerValue = isCustomRange ? parseDateInput(customFromDate) : computedRange?.from ?? null
  const toPickerValue = isCustomRange ? parseDateInput(customToDate) : computedRange?.to ?? null
  const isCustomRangeValid = !isCustomRange || (customFromDate !== '' && customToDate !== '' && customFromDate <= customToDate)

  const buildStatisticsPayload = useCallback(() => {
    const singleSiteId = effectiveSingleSiteId
    const payload: any = {
      timestamp: formatOffsetTimestamp(new Date()),
      timePeriod,
      chargerType: chargerType !== 'All' ? chargerType : undefined,
      chargerStatus: chargerStatus !== 'All' ? chargerStatus : undefined,
      siteIds: singleSiteId === null ? normalizedSelectedSiteIds ?? undefined : undefined,
      siteId: singleSiteId ?? undefined,
    }

    if (timePeriod === 'custom' && customFromDate && customToDate) {
      payload.from = new Date(`${customFromDate}T00:00:00.000`)
      payload.to = new Date(`${customToDate}T23:59:59.999`)
    }

    return payload
  }, [chargerStatus, chargerType, customFromDate, customToDate, effectiveSingleSiteId, normalizedSelectedSiteIds, timePeriod])

  const buildChartPayload = useCallback(() => {
    return {
      ...buildStatisticsPayload(),
      metric: selectedMetric,
      viewMode: chartViewMode,
    }
  }, [buildStatisticsPayload, chartViewMode, selectedMetric])

  const fetchChartDataForMetric = useCallback(
    async (metric: TileMetricKey) => {
      if (timePeriod === 'custom' && !isCustomRangeValid) {
        setCustomRangeError('Please select a valid custom date range.')
        return
      }

      setCustomRangeError('')
      try {
        const chartResponse = await getChartData({
          ...buildStatisticsPayload(),
          metric,
          viewMode: chartViewMode,
        })
        setChartSeries(Array.isArray(chartResponse.data) ? chartResponse.data : [])
      } catch {
        setChartSeries([])
      }
    },
    [buildStatisticsPayload, chartViewMode, isCustomRangeValid, timePeriod]
  )

  const handleMetricSelect = useCallback(
    (metric: TileMetricKey) => {
      setSelectedMetric(metric)
      if (!hasLoadedRef.current) return
      fetchChartDataForMetric(metric)
    },
    [fetchChartDataForMetric]
  )

  const fetchStatistics = useCallback(
    async (syncFirst = false, syncMode: 'full' | 'sessions' = 'full') => {
      if (timePeriod === 'custom' && !isCustomRangeValid) {
        setCustomRangeError('Please select a valid custom date range.')
        return
      }

      setCustomRangeError('')
      setLoading(true)
      try {
        if (syncFirst) {
          if (syncMode === 'sessions') {
            await syncSessionStatistics()
          } else {
            await syncStatistics()
          }
        }
        const [statisticsResponse, chartResponse] = await Promise.all([getStatistics(buildStatisticsPayload()), getChartData(buildChartPayload())])
        setStatistics(statisticsResponse.data)
        setChartSeries(Array.isArray(chartResponse.data) ? chartResponse.data : [])
        setLastReloadAt(new Date())
      } catch {
        setStatistics(null)
        setChartSeries([])
      } finally {
        setLoading(false)
      }
    },
    [buildChartPayload, buildStatisticsPayload, isCustomRangeValid, timePeriod]
  )

  const scheduleFetchStatistics = useCallback(
    (delayMs = 400) => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null
        fetchStatistics()
      }, delayMs)
    },
    [fetchStatistics]
  )

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (!hasLoadedRef.current) return
      if (timePeriod === 'custom' && !isCustomRangeValid) return
      fetchStatistics(true, 'sessions')
    }, 60_000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [fetchStatistics, isCustomRangeValid, timePeriod])

  useEffect(() => {
    if (hasLoadedRef.current) return
    fetchStatistics(true).finally(() => {
      hasLoadedRef.current = true
    })
  }, [fetchStatistics])

  useEffect(() => {
    if (!isDetailedViewAvailable && chartViewMode === 'detailed') {
      setChartViewMode('consolidated')
    }
  }, [chartViewMode, isDetailedViewAvailable])

  useEffect(() => {
    if (!hasLoadedRef.current) return
    if (timePeriod === 'custom') {
      if (!isCustomRangeValid) return
      if (suppressNextCustomChartFetchRef.current) {
        suppressNextCustomChartFetchRef.current = false
        return
      }
    }
    fetchChartDataForMetric(selectedMetric)
  }, [chartViewMode, fetchChartDataForMetric, isCustomRangeValid, selectedMetric, timePeriod])

  useEffect(() => {
    const previous = previousAutoFiltersRef.current
    const didAutoFilterChange =
      previous.chargerType !== chargerType ||
      previous.chargerStatus !== chargerStatus ||
      previous.selectedSiteScopeKey !== selectedSiteScopeKey
    const didTimePeriodChange = previous.timePeriod !== timePeriod

    previousAutoFiltersRef.current = {
      chargerType,
      chargerStatus,
      selectedSiteScopeKey,
      timePeriod,
    }

    if (!hasLoadedRef.current) return

    if (timePeriod === 'custom') {
      if (didAutoFilterChange && isCustomRangeValid) {
        scheduleFetchStatistics()
      }
      return
    }

    if (didAutoFilterChange || didTimePeriodChange) {
      scheduleFetchStatistics()
    }
  }, [chargerStatus, chargerType, isCustomRangeValid, scheduleFetchStatistics, selectedSiteScopeKey, timePeriod])

  const handleTimePeriodChange = (value: TimePeriod) => {
    if (value === 'custom' && timePeriod !== 'custom') {
      if (fromPickerValue !== null && toPickerValue !== null) {
        suppressNextCustomChartFetchRef.current = true
        setCustomFromDate(formatDateInput(fromPickerValue))
        setCustomToDate(formatDateInput(toPickerValue))
      } else {
        suppressNextCustomChartFetchRef.current = false
      }
    }

    setTimePeriod(value)
    if (value !== 'custom') {
      setCustomRangeError('')
    }
  }

  const preventDateTextInput = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (target.tagName !== 'INPUT') return

    const allowedKeys = ['Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (allowedKeys.includes(event.key)) return

    event.preventDefault()
  }, [])

  const preventDateBeforeInput = useCallback((event: React.FormEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).tagName === 'INPUT') {
      event.preventDefault()
    }
  }, [])

  const preventDatePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).tagName === 'INPUT') {
      event.preventDefault()
    }
  }, [])

  const metricValues = useMemo(
    () => ({
      activeKwh: parseMetricValue(statistics?.activeKwH, 'kwh'),
      completedSessions: parseMetricValue(statistics?.completedSessions, 'count'),
      revenueShare: parseMetricValue(statistics?.revenueShare, 'currency'),
      totalEnergy: parseMetricValue(statistics?.totalEnergy, 'kwh'),
      utilisationRate: parseMetricValue(statistics?.utilisationRate, 'percent'),
      co2Emissions: parseMetricValue(statistics?.cO2Emissions, 'kg'),
    }),
    [statistics]
  )
  const previousMetricValues = useMemo(
    () => ({
      completedSessions: parseMetricValue(statistics?.previousCompletedSessions, 'count'),
      revenueShare: parseMetricValue(statistics?.previousRevenueShare, 'currency'),
    }),
    [statistics]
  )
  const previousPeriodLabel = statistics?.previousPeriodLabel ?? 'Prev period'
  const formatUpdatedTime = useMemo(() => new Intl.DateTimeFormat('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true }), [])
  const updatedTimeLabel = lastReloadAt ? formatUpdatedTime.format(lastReloadAt).toUpperCase() : '--:--'

  const metricMeta: Record<TileMetricKey, { title: string; strokeColor: string; format: (value: number) => string }> = {
    activeKwh: { title: 'Active kWh', strokeColor: '#ff9800', format: (value) => `${toDecimal(value)} kWh` },
    completedSessions: { title: 'Completed Sessions', strokeColor: '#43b649', format: (value) => toCount(value) },
    revenueShare: { title: 'Revenue Share', strokeColor: '#43b649', format: (value) => toCurrency(value) },
    totalEnergy: { title: 'Total Energy', strokeColor: '#ffb300', format: (value) => `${toDecimal(value)} kWh` },
    utilisationRate: { title: 'Utilisation Rate', strokeColor: '#673ab7', format: (value) => toPercent(value) },
    co2Emissions: { title: 'CO2 Emissions', strokeColor: '#388e3c', format: (value) => toKg(value) },
  }
  const selectedMetricInfo = metricMeta[selectedMetric]
  const selectedMetricValue = metricValues[selectedMetric]

  const parsedChartRows = useMemo(() => {
    return chartSeries
      .map((point) => {
        const parsedDate = new Date(point.date)
        return {
          date: parsedDate,
          value: Number(point.value),
          seriesKey: point.seriesKey ?? '',
          seriesName: point.seriesName ?? '',
        }
      })
      .filter((point) => Number.isFinite(point.value) && !Number.isNaN(point.date.getTime()))
      .sort((left, right) => left.date.getTime() - right.date.getTime())
  }, [chartSeries])

  const timelinePoints = useMemo(() => {
    const uniqueByTime = new Map<number, Date>()
    parsedChartRows.forEach((point) => {
      uniqueByTime.set(point.date.getTime(), point.date)
    })
    return Array.from(uniqueByTime.values()).sort((left, right) => left.getTime() - right.getTime())
  }, [parsedChartRows])

  const detailedChartSeries = useMemo<StackedBarChartSeries[]>(() => {
    if (chartViewMode !== 'detailed' || !isDetailedViewAvailable) {
      return []
    }

    const groupedSeries = new Map<string, { name: string; rows: typeof parsedChartRows }>()
    parsedChartRows.forEach((row) => {
      const key = row.seriesKey || row.seriesName || 'unassigned'
      const existing = groupedSeries.get(key)
      if (existing) {
        existing.rows.push(row)
        return
      }

      groupedSeries.set(key, {
        name: row.seriesName || row.seriesKey || 'Unnamed Charger',
        rows: [row],
      })
    })

    const palette = buildRelatedColorPalette(groupedSeries.size, selectedMetricInfo.strokeColor)
    return Array.from(groupedSeries.entries())
      .sort((left, right) => left[1].name.localeCompare(right[1].name))
      .map(([key, value], index) => {
        const points: StackedBarChartPoint[] = value.rows
          .map((row) => {
            const xIndex = timelinePoints.findIndex((timelinePoint) => timelinePoint.getTime() === row.date.getTime())
            return {
              x: xIndex + 1,
              y: Number(row.value.toFixed(2)),
              seriesKey: key,
              seriesName: value.name,
            }
          })
          .filter((point) => point.x > 0)

        return {
          key,
          name: value.name,
          color: palette[index],
          data: points,
        }
      })
  }, [chartViewMode, isDetailedViewAvailable, parsedChartRows, selectedMetricInfo.strokeColor, timelinePoints])

  const consolidatedChartData = useMemo<StackedBarChartPoint[]>(() => {
    return timelinePoints.map((point, index) => {
      const matchingRow = parsedChartRows.find((row) => row.date.getTime() === point.getTime())
      return {
        x: index + 1,
        y: Number((matchingRow?.value ?? 0).toFixed(2)),
      }
    })
  }, [parsedChartRows, timelinePoints])

  const hasIntradayPoints = useMemo(() => {
    return timelinePoints.some((point) => point.getHours() !== 0 || point.getMinutes() !== 0 || point.getSeconds() !== 0)
  }, [timelinePoints])

  const isMonthGrouped = useMemo(() => {
    if (timelinePoints.length === 0 || hasIntradayPoints) return false
    const nonMonthStartPoints = timelinePoints
      .map((point, index) => ({ dayOfMonth: point.getDate(), index }))
      .filter((point) => point.dayOfMonth !== 1)

    if (nonMonthStartPoints.length === 0) return true
    return nonMonthStartPoints.length === 1 && nonMonthStartPoints[0].index === timelinePoints.length - 1
  }, [hasIntradayPoints, timelinePoints])

  const isSingleDayRange = useMemo(() => {
    if (timelinePoints.length === 0) return false
    const firstDate = timelinePoints[0]
    const lastDate = timelinePoints[timelinePoints.length - 1]
    return (
      firstDate.getFullYear() === lastDate.getFullYear() &&
      firstDate.getMonth() === lastDate.getMonth() &&
      firstDate.getDate() === lastDate.getDate()
    )
  }, [timelinePoints])

  const chartPointCount = timelinePoints.length

  const chartSpanDays = useMemo(() => {
    if (timelinePoints.length <= 1) return 1
    const first = timelinePoints[0].getTime()
    const last = timelinePoints[timelinePoints.length - 1].getTime()
    return Math.max(1, Math.ceil((last - first) / (1000 * 60 * 60 * 24)))
  }, [timelinePoints])

  const chartBucketGapDays = useMemo(() => {
    if (timelinePoints.length <= 1) return 0
    const dayGaps = timelinePoints
      .slice(1)
      .map((point, index) => (point.getTime() - timelinePoints[index].getTime()) / (1000 * 60 * 60 * 24))
      .filter((gap) => gap > 0)

    return dayGaps.length > 0 ? Math.min(...dayGaps) : 0
  }, [timelinePoints])

  const chartAxisMode = useMemo<ChartAxisMode>(() => {
    if (hasIntradayPoints && (isSingleDayRange || chartSpanDays <= 2)) return 'time'
    if (chartBucketGapDays >= 360) return 'year'
    if (chartBucketGapDays >= 80) return 'quarter'
    if (chartBucketGapDays >= 25) return 'month'
    if (chartBucketGapDays >= 6) return 'week'
    return 'day'
  }, [chartBucketGapDays, chartSpanDays, hasIntradayPoints, isSingleDayRange])

  const chartTickValues = useMemo(() => {
    if (chartPointCount === 0) return [] as number[]
    return Array.from({ length: chartPointCount }, (_, index) => index + 1)
  }, [chartPointCount])

  const formatDayTick = useMemo(() => new Intl.DateTimeFormat('en-AU', { month: 'short', day: 'numeric' }), [])
  const formatMonthTick = useMemo(() => new Intl.DateTimeFormat('en-AU', { month: 'short', year: '2-digit' }), [])
  const formatTimeTick = useMemo(() => new Intl.DateTimeFormat('en-AU', { hour: 'numeric' }), [])
  const skewChartXAxisLabels = chartTickValues.length > 16
  const getChartDisplayDate = useCallback(
    (date: Date, index: number) => {
      if (chartAxisMode !== 'week' || index !== timelinePoints.length - 1 || !toPickerValue || toPickerValue.getDay() === 0) {
        return date
      }

      const bucketDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const rangeToDateOnly = new Date(toPickerValue.getFullYear(), toPickerValue.getMonth(), toPickerValue.getDate())
      return rangeToDateOnly.getTime() <= bucketDateOnly.getTime() ? toPickerValue : date
    },
    [chartAxisMode, timelinePoints.length, toPickerValue]
  )

  const formatChartTick = useCallback(
    (tick: number | string | Date) => {
      const numericTick = typeof tick === 'number' ? Math.round(tick) : Number(tick)
      if (!Number.isFinite(numericTick)) return ''
      const index = numericTick - 1
      if (index < 0 || index >= timelinePoints.length) return ''
      const date = getChartDisplayDate(timelinePoints[index], index)
      if (chartAxisMode === 'time') return formatTimeTick.format(date)
      if (chartAxisMode === 'year') return date.getFullYear().toString()
      if (chartAxisMode === 'quarter') return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear().toString().slice(-2)}`
      if (chartAxisMode === 'month') {
        return formatMonthTick.format(date)
      }
      return formatDayTick.format(date)
    },
    [chartAxisMode, formatDayTick, formatMonthTick, formatTimeTick, getChartDisplayDate, timelinePoints]
  )

  const formatYAxisTick = useCallback((tick: number | string | Date) => {
    const numericTick = typeof tick === 'number' ? tick : Number(tick)
    if (!Number.isFinite(numericTick)) return ''

    const toSuffix = (divisor: number, suffix: string) => {
      const scaled = numericTick / divisor
      const precision = Math.abs(scaled) >= 10 ? 0 : 1
      return `${Number(scaled.toFixed(precision))}${suffix}`
    }

    const absolute = Math.abs(numericTick)
    if (absolute >= 1_000_000_000) return toSuffix(1_000_000_000, 'b')
    if (absolute >= 1_000_000) return toSuffix(1_000_000, 'm')
    if (absolute >= 1_000) return toSuffix(1_000, 'k')

    if (Number.isInteger(numericTick)) return numericTick.toString()
    return Number(numericTick.toFixed(2)).toString()
  }, [])

  const formatTooltipDate = useMemo(() => new Intl.DateTimeFormat('en-AU', { month: 'short', day: 'numeric', year: 'numeric' }), [])
  const formatTooltipMonthYear = useMemo(() => new Intl.DateTimeFormat('en-AU', { month: 'short', year: 'numeric' }), [])
  const formatTooltipDateTime = useMemo(
    () => new Intl.DateTimeFormat('en-AU', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
    []
  )

  const formatChartTooltip = useCallback(
    (point: { x: number | string | Date; y: number }) => {
      const numericX = typeof point.x === 'number' ? Math.round(point.x) : Number(point.x)
      if (!Number.isFinite(numericX)) {
        return selectedMetricInfo.format(point.y)
      }

      const index = numericX - 1
      const activeSeries = point && 'seriesName' in point ? point.seriesName : ''
      if (index < 0 || index >= timelinePoints.length) {
        return selectedMetricInfo.format(point.y)
      }

      const date = getChartDisplayDate(timelinePoints[index], index)
      let dateLabel = formatTooltipDate.format(date)
      if (hasIntradayPoints) {
        dateLabel = formatTooltipDateTime.format(date)
      } else if (chartAxisMode === 'year') {
        dateLabel = date.getFullYear().toString()
      } else if (chartAxisMode === 'quarter') {
        dateLabel = `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`
      } else if (isMonthGrouped) {
        dateLabel = formatTooltipMonthYear.format(date)
      }
      return activeSeries
        ? `${activeSeries}\n${dateLabel}\n${selectedMetricInfo.format(point.y)}`
        : `${dateLabel}\n${selectedMetricInfo.format(point.y)}`
    },
    [chartAxisMode, formatTooltipDate, formatTooltipDateTime, formatTooltipMonthYear, getChartDisplayDate, hasIntradayPoints, isMonthGrouped, selectedMetricInfo, timelinePoints]
  )

  const interactiveTileStyle = (metric: TileMetricKey): React.CSSProperties => {
    const isSelected = selectedMetric === metric
    const isHovered = hoveredMetric === metric
    return {
      ...cardStyle,
      height: '100%',
      cursor: 'pointer',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      border: isSelected ? '2px solid #44d62c' : isHovered ? '1px solid #44d62c' : '1px solid #ccc',
      boxShadow: isSelected ? '0 4px 10px #0002' : isHovered ? '0 2px 8px #0002' : cardStyle.boxShadow,
      transform: isHovered ? 'translateY(-2px)' : 'none',
    }
  }

  const nonInteractiveTileStyle: React.CSSProperties = {
    ...cardStyle,
    height: '100%',
  }

  const getChangeTrendDirection = (changeText?: string) => {
    const normalized = (changeText || '').toLowerCase()
    if (normalized.includes('decrease')) return 'decrease'
    if (normalized.includes('increase')) return 'increase'
    return null
  }

  const renderChangeTextColor = (changeText?: string) => {
    if (loading) return '#444'
    const trendDirection = getChangeTrendDirection(changeText)
    if (trendDirection === 'decrease') return '#F1416C'
    if (trendDirection === 'increase') return '#43b649'
    return '#444'
  }

  const renderTrendIcon = (changeText?: string) => {
    if (loading) return null
    const trendDirection = getChangeTrendDirection(changeText)
    if (trendDirection === 'increase') {
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
          style={{ width: 14, height: 14, flex: '0 0 auto' }}
        >
          <polyline points='22 7 13.5 15.5 8.5 10.5 2 17'></polyline>
          <polyline points='16 7 22 7 22 13'></polyline>
        </svg>
      )
    }
    if (trendDirection === 'decrease') {
      return (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          aria-hidden='true'
          style={{ width: 14, height: 14, flex: '0 0 auto' }}
        >
          <polyline points='22 17 13.5 8.5 8.5 13.5 2 7'></polyline>
          <polyline points='16 17 22 17 22 11'></polyline>
        </svg>
      )
    }
    return null
  }

  const renderComparisonSummary = (changeText: string | undefined, previousValue: string) => {
    const previousText = loading ? '...' : `${previousPeriodLabel}: ${previousValue}`
    return (
      <div
        style={{
          color: renderChangeTextColor(changeText),
          fontSize: 12,
          marginTop: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={loading ? undefined : `${changeText ?? '0%'} | ${previousText}`}
      >
        {renderTrendIcon(changeText)}
        <span>{loading ? '...' : changeText ?? '0%'}</span>
        <span style={{ color: '#A1A5B7' }}>|</span>
        <span style={{ color: '#7E8299', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>{previousText}</span>
      </div>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({ id: 'MENU.DASHBOARD', defaultMessage: 'Dashboard' })}</PageTitle>
      <div className='card card-custom'>
        <div className='card-body' style={{ minWidth: 'calc(100vw - 400px)' }}>
          <div className='row' style={{ background: '#fff', paddingBottom: 10, marginBottom: 20, boxShadow: '0 4px 4px -2px rgba(0, 0, 0, 0.07)' }}>
            <div className='col-12 col-sm-6 col-md-2 mb-2'>
              <div className='form-group'>
                <label className='form-label mb-1' htmlFor='filter-charger-type'>Charger Type</label>
                <select
                  id='filter-charger-type'
                  value={chargerType}
                  onChange={(event) => setChargerType(event.target.value)}
                  className='form-select form-select-sm'
                  style={filterControlStyle}
                  disabled={loading}
                >
                  <option value='All'>All</option>
                  <option value='Public'>Public</option>
                  <option value='Private'>Private</option>
                  <option value='Guest'>Guest</option>
                </select>
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-2 mb-2'>
              <div className='form-group'>
                <label className='form-label mb-1' htmlFor='filter-charger-status'>Charger Status</label>
                <select
                  id='filter-charger-status'
                  value={chargerStatus}
                  onChange={(event) => setChargerStatus(event.target.value)}
                  className='form-select form-select-sm'
                  style={filterControlStyle}
                  disabled={loading}
                >
                  <option value='All'>All</option>
                  <option value='Online'>Online</option>
                  <option value='Offline'>Offline</option>
                </select>
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-2 mb-2'>
              <div className='form-group'>
                <label className='form-label mb-1' htmlFor='filter-time-period'>Time Period</label>
                <select
                  id='filter-time-period'
                  value={timePeriod}
                  onChange={(event) => handleTimePeriodChange(event.target.value as TimePeriod)}
                  className='form-select form-select-sm'
                  style={filterControlStyle}
                  disabled={loading}
                >
                  <option value='today'>Today</option>
                  <option value='last7days'>Last 7 days</option>
                  <option value='lastmonth'>Last month</option>
                  <option value='lastyear'>Last year</option>
                  <option value='custom'>Custom range</option>
                </select>
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-2 mb-2'>
              <div className='form-group'>
                <label className='form-label mb-1' htmlFor='filter-from-date'>From</label>
                <div
                  style={{ width: '100%' }}
                  onBeforeInputCapture={preventDateBeforeInput}
                  onKeyDownCapture={preventDateTextInput}
                  onPasteCapture={preventDatePaste}
                >
                  <DatePicker
                    id='filter-from-date'
                    format='dd/MM/yyyy'
                    value={fromPickerValue}
                    onChange={(event: any) => setCustomFromDate(event.value ? formatDateInput(event.value as Date) : '')}
                    className='w-100 form-control'
                    disabled={!isCustomRange || loading}
                  />
                </div>
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-2 mb-2'>
              <div className='form-group'>
                <label className='form-label mb-1' htmlFor='filter-to-date'>To</label>
                <div
                  style={{ width: '100%' }}
                  onBeforeInputCapture={preventDateBeforeInput}
                  onKeyDownCapture={preventDateTextInput}
                  onPasteCapture={preventDatePaste}
                >
                  <DatePicker
                    id='filter-to-date'
                    format='dd/MM/yyyy'
                    value={toPickerValue}
                    onChange={(event: any) => setCustomToDate(event.value ? formatDateInput(event.value as Date) : '')}
                    className='w-100'
                    disabled={!isCustomRange || loading}
                  />
                </div>
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-2 mb-2 d-flex align-items-end'>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  justifyContent: isCustomRange ? 'space-between' : 'flex-end',
                }}
              >
                {isCustomRange && (
                  <button
                    type='button'
                    className='btn btn-sm btn-primary'
                    onClick={() => fetchStatistics()}
                    disabled={loading}
                    style={{ flex: '0 0 calc(50% - 6px)' }}
                  >
                    Apply
                  </button>
                )}
                <div
                  style={{
                    color: '#7E8299',
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                    flex: isCustomRange ? '0 0 calc(50% - 6px)' : '0 0 auto',
                    textAlign: isCustomRange ? 'left' : 'right',
                  }}
                >
                  Updated: <span style={{ color: '#181C32', fontWeight: 600 }}>{updatedTimeLabel}</span>
                </div>
              </div>
            </div>
            {customRangeError && (
              <div className='col-12' style={{ color: '#F1416C', fontSize: 12, marginTop: 4 }}>
                {customRangeError}
              </div>
            )}
          </div>

          <div className='row'>
            <div className='col-12 col-sm-6 col-md-3' style={{ padding: 6 }}>
              <div style={nonInteractiveTileStyle}>
                <div style={tileTitleRowStyle}>
                  <i className='fas fa-shield-alt' style={tileIconStyle('#43b649')}></i> System Health
                </div>
                <div
                  style={tileValueStyle(!loading && parseNumber(statistics?.offlineChargers) > 0 ? '#F1416C' : '#43b649')}
                >
                  {loading ? '...' : statistics?.offlineChargers ?? '0'}
                </div>
                <div style={tileSubtitleStyle}>
                  Offline out of <b>{loading ? '...' : statistics?.allChargers ?? '0'}</b> total
                </div>
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-3' style={{ padding: 6 }}>
              <div style={nonInteractiveTileStyle}>
                <div style={tileTitleRowStyle}>
                  <i className='fas fa-sync-alt' style={tileIconStyle('#2196f3')}></i> Active Sessions
                </div>
                <div style={tileValueStyle('#2196f3')}>
                  {loading ? '...' : statistics?.activeSessions ?? '0'}
                </div>
                <div style={tileSubtitleStyle}>Currently Charging</div>
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-3' style={{ padding: 6 }}>
              <div style={nonInteractiveTileStyle}>
                <div style={tileTitleRowStyle}>
                  <i className='fas fa-bolt' style={tileIconStyle('#ff9800')}></i> Active kWh
                </div>
                <div style={tileValueStyle('#ff9800')}>
                  {loading ? '...' : toDecimal(metricValues.activeKwh)} <span style={{ fontSize: '0.5em' }}>kWh</span>
                </div>
                <div style={tileSubtitleStyle}>From Active Sessions</div>
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-3' style={{ padding: 6 }}>
              <div style={nonInteractiveTileStyle}>
                <div style={tileTitleRowStyle}>
                  <i className='fas fa-chart-bar' style={tileIconStyle('#673ab7')}></i> Utilisation Rate
                </div>
                <div style={tileValueStyle('#673ab7')}>
                  {loading ? '...' : toPercent(metricValues.utilisationRate)}
                </div>
                <div style={tileSubtitleStyle}>Current Network Utilisation</div>
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-12 col-sm-6 col-md-3' style={{ padding: 6 }}>
              <div
                style={interactiveTileStyle('completedSessions')}
                onMouseEnter={() => setHoveredMetric('completedSessions')}
                onMouseLeave={() => setHoveredMetric(null)}
                onClick={() => handleMetricSelect('completedSessions')}
                role='button'
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') handleMetricSelect('completedSessions')
                }}
              >
                <div style={tileTitleRowStyle}>
                  <i className='fas fa-shuttle-van' style={tileIconStyle('#43b649')}></i> Completed Sessions
                </div>
                <div style={tileValueStyle('#555')}>
                  {loading ? '...' : toCount(metricValues.completedSessions)}
                </div>
                {renderComparisonSummary(statistics?.completedSessionsChange, toCount(previousMetricValues.completedSessions))}
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-3' style={{ padding: 6 }}>
              <div
                style={interactiveTileStyle('totalEnergy')}
                onMouseEnter={() => setHoveredMetric('totalEnergy')}
                onMouseLeave={() => setHoveredMetric(null)}
                onClick={() => handleMetricSelect('totalEnergy')}
                role='button'
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') handleMetricSelect('totalEnergy')
                }}
              >
                <div style={tileTitleRowStyle}>
                  <i className='fas fa-bolt' style={tileIconStyle('#ffb300')}></i> Total Energy
                </div>
                <div style={tileValueStyle('#555')}>
                  {loading ? '...' : toDecimal(metricValues.totalEnergy)} <span style={{ fontSize: '0.5em' }}>kWh</span>
                </div>
                <div style={tileSubtitleStyle}>From Completed Sessions only</div>
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-3' style={{ padding: 6 }}>
              <div
                style={interactiveTileStyle('revenueShare')}
                onMouseEnter={() => setHoveredMetric('revenueShare')}
                onMouseLeave={() => setHoveredMetric(null)}
                onClick={() => handleMetricSelect('revenueShare')}
                role='button'
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') handleMetricSelect('revenueShare')
                }}
              >
                <div style={tileTitleRowStyle}>
                  <i className='fas fa-dollar-sign' style={tileIconStyle('#43b649')}></i> Revenue Share
                </div>
                <div style={tileValueStyle('#555')}>
                  {loading ? '...' : toCurrency(metricValues.revenueShare)}
                </div>
                {renderComparisonSummary(statistics?.revenueShareChange, toCurrency(previousMetricValues.revenueShare))}
              </div>
            </div>
            <div className='col-12 col-sm-6 col-md-3' style={{ padding: 6 }}>
              <div
                style={interactiveTileStyle('co2Emissions')}
                onMouseEnter={() => setHoveredMetric('co2Emissions')}
                onMouseLeave={() => setHoveredMetric(null)}
                onClick={() => handleMetricSelect('co2Emissions')}
                role='button'
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') handleMetricSelect('co2Emissions')
                }}
              >
                <div style={tileTitleRowStyle}>
                  <i className='fas fa-seedling' style={tileIconStyle('#388e3c')}></i> CO₂ Emissions Avoided
                </div>
                <div style={tileValueStyle('#555')}>
                  {loading ? '...' : toDecimal(metricValues.co2Emissions)} <span style={{ fontSize: '0.5em' }}>kg</span>
                </div>
                <div style={tileSubtitleStyle}>CO₂ equivalent (AUS avg 0.53)</div>
              </div>
            </div>
          </div>

          <div style={{ ...cardStyle, marginTop: 16, paddingBottom: 0, background: '#fff' }}>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <div className='d-flex align-items-center gap-3'>
                <div style={{ fontWeight: 600, fontSize: 20, whiteSpace: 'nowrap' }}>{selectedMetricInfo.title} Trend</div>
                {isDetailedViewAvailable && (
                  <select
                    value={chartViewMode}
                    onChange={(event) => setChartViewMode(event.target.value as DashboardChartViewMode)}
                    className='form-select form-select-sm'
                    style={{ ...filterControlStyle, minWidth: 160 }}
                  >
                    <option value='consolidated'>Consolidated View</option>
                    <option value='detailed'>Detailed View</option>
                  </select>
                )}
              </div>
              <div style={{ fontSize: 16 }}>
                <span style={{ fontWeight: 900, color: '#777' }}>Total: </span>
                <span style={{ fontWeight: 600, color: selectedMetricInfo.strokeColor }}>{selectedMetricInfo.format(selectedMetricValue)}</span>
              </div>
            </div>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <StackedBarChart
                data={consolidatedChartData}
                series={chartViewMode === 'detailed' ? detailedChartSeries : undefined}
                height={260}
                fillColor={selectedMetricInfo.strokeColor}
                xTickValues={chartTickValues}
                xTickFormat={formatChartTick}
                yTickFormat={formatYAxisTick}
                tooltipFormatter={formatChartTooltip}
                skewXAxisLabels={skewChartXAxisLabels}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DashboardPage
